/**
 * Country code normalization utilities
 * Handles conversion between different country code formats (ISO 3166-1 alpha-2, alpha-3, and full names)
 */

/**
 * Map of 3-character country codes to 2-character ISO 3166-1 alpha-2 codes
 * Only includes countries that commonly appear in payment gateway responses
 */
const COUNTRY_CODE_MAP: Record<string, string> = {
  // Common 3-letter codes
  USA: "US",
  CAN: "CA",
  GBR: "GB",
  AUS: "AU",
  NZL: "NZ",
  DEU: "DE",
  FRA: "FR",
  ITA: "IT",
  ESP: "ES",
  NLD: "NL",
  BEL: "BE",
  CHE: "CH",
  AUT: "AT",
  IRL: "IE",
  DNK: "DK",
  SWE: "SE",
  NOR: "NO",
  FIN: "FI",
  POL: "PL",
  CZE: "CZ",
  HUN: "HU",
  ROU: "RO",
  BGR: "BG",
  HRV: "HR",
  SVN: "SI",
  SVK: "SK",
  LTU: "LT",
  LVA: "LV",
  EST: "EE",
  GRC: "GR",
  PRT: "PT",
  MEX: "MX",
  BRA: "BR",
  ARG: "AR",
  CHL: "CL",
  COL: "CO",
  PER: "PE",
  VEN: "VE",
  JPN: "JP",
  CHN: "CN",
  KOR: "KR",
  IND: "IN",
  IDN: "ID",
  THA: "TH",
  MYS: "MY",
  SGP: "SG",
  PHL: "PH",
  VNM: "VN",
  ZAF: "ZA",
  EGY: "EG",
  NGA: "NG",
  KEN: "KE",
  MAR: "MA",
  TUR: "TR",
  ISR: "IL",
  SAU: "SA",
  ARE: "AE",
  RUS: "RU",
  UKR: "UA",
};

/**
 * Map of full country names to 2-character ISO 3166-1 alpha-2 codes
 * Only includes countries that commonly appear in payment gateway responses
 */
const COUNTRY_NAME_MAP: Record<string, string> = {
  "United States": "US",
  "United States of America": "US",
  USA: "US",
  Canada: "CA",
  "United Kingdom": "GB",
  "Great Britain": "GB",
  UK: "GB",
  Australia: "AU",
  "New Zealand": "NZ",
  Germany: "DE",
  France: "FR",
  Italy: "IT",
  Spain: "ES",
  Netherlands: "NL",
  Belgium: "BE",
  Switzerland: "CH",
  Austria: "AT",
  Ireland: "IE",
  Denmark: "DK",
  Sweden: "SE",
  Norway: "NO",
  Finland: "FI",
  Poland: "PL",
  "Czech Republic": "CZ",
  Hungary: "HU",
  Romania: "RO",
  Bulgaria: "BG",
  Croatia: "HR",
  Slovenia: "SI",
  Slovakia: "SK",
  Lithuania: "LT",
  Latvia: "LV",
  Estonia: "EE",
  Greece: "GR",
  Portugal: "PT",
  Mexico: "MX",
  Brazil: "BR",
  Argentina: "AR",
  Chile: "CL",
  Colombia: "CO",
  Peru: "PE",
  Venezuela: "VE",
  Japan: "JP",
  China: "CN",
  "South Korea": "KR",
  Korea: "KR",
  India: "IN",
  Indonesia: "ID",
  Thailand: "TH",
  Malaysia: "MY",
  Singapore: "SG",
  Philippines: "PH",
  Vietnam: "VN",
  "South Africa": "ZA",
  Egypt: "EG",
  Nigeria: "NG",
  Kenya: "KE",
  Morocco: "MA",
  Turkey: "TR",
  Israel: "IL",
  "Saudi Arabia": "SA",
  "United Arab Emirates": "AE",
  UAE: "AE",
  Russia: "RU",
  Ukraine: "UA",
};

/**
 * Normalize a country code to 2-character ISO 3166-1 alpha-2 format
 * Handles various input formats:
 * - 2-character codes (US, CA, GB) - returned as-is
 * - 3-character codes (USA, CAN, GBR) - converted to 2-char
 * - Full country names (United States, Canada) - converted to 2-char
 *
 * @param countryCode Country code in any format (2-char, 3-char, or full name)
 * @returns 2-character ISO 3166-1 alpha-2 country code, or original value if no mapping found
 *
 * @example
 * normalizeCountryCode("USA") // Returns "US"
 * normalizeCountryCode("United States") // Returns "US"
 * normalizeCountryCode("US") // Returns "US"
 * normalizeCountryCode("GBR") // Returns "GB"
 */
export function normalizeCountryCode(
  countryCode: string | null | undefined,
): string | undefined {
  if (!countryCode) {
    return undefined;
  }

  const trimmed = countryCode.trim();

  // If already 2 characters and uppercase, assume it's already ISO alpha-2
  if (trimmed.length === 2) {
    return trimmed.toUpperCase();
  }

  // Check 3-letter code map
  const alpha3Upper = trimmed.toUpperCase();
  if (COUNTRY_CODE_MAP[alpha3Upper]) {
    return COUNTRY_CODE_MAP[alpha3Upper];
  }

  // Check full name map (case-insensitive)
  const nameKey = Object.keys(COUNTRY_NAME_MAP).find(
    (key) => key.toLowerCase() === trimmed.toLowerCase(),
  );
  if (nameKey) {
    return COUNTRY_NAME_MAP[nameKey];
  }

  // No mapping found - return original value
  // (could be a valid 2-char code we don't know about, or an invalid value)
  return trimmed;
}

/**
 * Normalize expiration date from various formats to YYYYMM format (no separators)
 * Handles common formats from payment gateways:
 * - "YYYY-MM" (Authorize.Net) -> "YYYYMM"
 * - "MM/YYYY" -> "YYYYMM"
 * - "MM-YYYY" -> "YYYYMM"
 * - "MMYY" -> "YYYYMM"
 * - "MM/YY" -> "YYYYMM"
 * - "YYYYMM" -> "YYYYMM" (already normalized)
 *
 * @param expiration Expiration date in any format
 * @returns Expiration date in YYYYMM format, or original value if format not recognized
 *
 * @example
 * normalizeExpiration("2025-01") // Returns "202501"
 * normalizeExpiration("01/2025") // Returns "202501"
 * normalizeExpiration("0125") // Returns "202501"
 * normalizeExpiration("202501") // Returns "202501"
 */
export function normalizeExpiration(
  expiration: string | null | undefined,
): string | undefined {
  if (!expiration) {
    return undefined;
  }

  const trimmed = expiration.trim();

  // YYYY-MM format (Authorize.Net: "2025-01")
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return trimmed.replace("-", "");
  }

  // MM/YYYY or MM-YYYY format
  if (/^\d{2}[/-]\d{4}$/.test(trimmed)) {
    const parts = trimmed.split(/[/-]/);
    return `${parts[1]}${parts[0]}`; // Convert to YYYYMM
  }

  // MMYY format (convert to YYYYMM by adding century)
  if (/^\d{4}$/.test(trimmed)) {
    const mm = trimmed.substring(0, 2);
    const yy = trimmed.substring(2, 4);
    const century = parseInt(yy) >= 50 ? "19" : "20"; // Assume 50-99 = 1950-1999, 00-49 = 2000-2049
    return `${century}${yy}${mm}`;
  }

  // MM/YY or MM-YY format
  if (/^\d{2}[/-]\d{2}$/.test(trimmed)) {
    const parts = trimmed.split(/[/-]/);
    const mm = parts[0];
    const yy = parts[1];
    const century = parseInt(yy) >= 50 ? "19" : "20";
    return `${century}${yy}${mm}`;
  }

  // YYYYMM format (already normalized)
  if (/^\d{6}$/.test(trimmed)) {
    return trimmed;
  }

  // Format not recognized - return original
  return trimmed;
}
